/* eslint-disable no-console */
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Button, Tag, Tooltip, notification, Select, Radio, InputNumber } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COPY TO CLIP BOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBackward,
  faForward,
  faPause,
  faPlay,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateReplayFilterBankId,
  updateReplayScannedBankIds,
} from "../../../redux/monitoring/monitoringSlice";
// COMPONENTS
import ReplayFilesPrettyJson from "./ReplayFilesPrettyJson";
import IconDefinitionRules from "../../edgeManagement/adaptersConfiguration/iconDefinition/IconDefinitionRules";
// import ReplayFilesBank from "./ReplayFilesBank";
import BankDetailsCard from "../../edgeManagement/banksConfiguration/BanksDetailCard";
import ControlPanelStateView from "../ControlPanel/ControlPanelStateView";
// UTILS
import { replayMoveKind } from "./replayPlayhead";
import { decodeReplayFile, ReplayRecord } from "../../../utils/binaryReplayCodec";
import { uuidToString, stringToUuidT, EMPTY_UUID } from "../../../utils/uuid";
import { formatUuidT } from "../../../flatbuffers/display";
import { bitmaskPrefix } from "../../../utils/typedKeys";
import { msgTypeLabel, resolveValue } from "../../../flatbuffers/decoders/applyDeltaToState";
import { Usage } from "../../../flatbuffers/generated/usage/usage";
import { UsageMap } from "../../../types/monitoringTypes";
import {
  directionStringToEnum,
  doorZoneStringToEnum,
  escControlStringToEnum,
} from "../../../flatbuffers/types/enumDisplayUtils";
// MONITORING STATE
import {
  useBankDispatch,
  useLastBankMessage,
  useLastLiftMessage,
} from "../socketMessageState/BankMessagesContext";
// CUSTOM HOOKS
import useGetOneBank from "../../../customHooks/GET_requests/useGetOneBank";
// INTERFACES
import { RootState } from "../../../app/store";
import {
  NO_ACK_VERDICT,
  SocketMessageInterface,
  LayoutInterface,
} from "../../../interfaces/Monitoring.interface";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { BLANK_BANK } from "../../../constants/bank";
import { BLANK_ADAPTER } from "../../../constants/adapter";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";

/** JSON replacer that converts BigInt (inside UuidT) to string. */
function bigIntReplacer(_key: string, value: unknown): unknown {
  return typeof value === "bigint" ? value.toString() : value;
}

/**
 * Normalize a legacy JSON replay record (snake_case) into a camelCase
 * SocketMessageInterface.  Binary replay files are already normalized by
 * binaryReplayCodec, but JSON files parsed with JSON.parse retain their
 * original key names.
 */
function normalizeJsonRecord(raw: any): SocketMessageInterface {
  const r = raw as Record<string, any>;
  const ds = r.doorStatus ?? r.door_status ?? {};
  const cc = r.carCalls ?? r.car_calls ?? { front: [], rear: [], frontLock: [], rearLock: [] };
  const hcRaw = r.hallCalls ?? r.hall_calls ?? {};
  const empty = { front: [], rear: [], frontLock: [], rearLock: [] };
  const normPC = (obj: any) => ({
    front: obj?.front ?? [],
    rear: obj?.rear ?? [],
    frontLock: obj?.frontLock ?? obj?.front_lock ?? [],
    rearLock: obj?.rearLock ?? obj?.rear_lock ?? [],
  });
  const hc = {
    up: normPC(hcRaw.up),
    down: normPC(hcRaw.down),
    upAssigned: normPC(hcRaw.upAssigned ?? hcRaw.up_assigned ?? empty),
    downAssigned: normPC(hcRaw.downAssigned ?? hcRaw.down_assigned ?? empty),
  };
  const dir =
    typeof r.direction === "string" ? directionStringToEnum(r.direction) : (r.direction ?? null);
  const dz =
    typeof (r.doorZone ?? r.door_zone) === "string"
      ? doorZoneStringToEnum(r.doorZone ?? r.door_zone)
      : (r.doorZone ?? r.door_zone ?? null);
  const esc =
    typeof (r.escalatorControl ?? r.escalator_control) === "string"
      ? escControlStringToEnum(r.escalatorControl ?? r.escalator_control)
      : (r.escalatorControl ?? r.escalator_control ?? null);

  return {
    bankId: r.bankId ?? r.bank_id,
    liftId: r.liftId ?? r.lift_id,
    serviceMode: r.serviceMode ?? r.service_mode ?? null,
    direction: dir,
    carCalls: normPC(cc),
    hallCalls: hc,
    position: r.position ?? 0,
    faults: r.faults ?? false,
    activeFaults: r.activeFaults ?? r.active_faults ?? [],
    doorStatus: {
      frontOpen: ds.frontOpen ?? ds.front_open ?? false,
      rearOpen: ds.rearOpen ?? ds.rear_open ?? false,
      frontStalled: ds.frontStalled ?? ds.front_stalled ?? false,
      rearStalled: ds.rearStalled ?? ds.rear_stalled ?? false,
    },
    doorZone: dz,
    levelling: r.levelling ?? false,
    escalatorControl: esc,
    msg: r.msg ?? { ...BLANK_SOCKET_MESSAGE.msg },
    // Legacy captures made before `connected` existed carry no such field at
    // all — that's an unknown, not an observed disconnect, so default to
    // true (the record IS a capture of what was, by definition, a live
    // session) rather than false. `??` only substitutes for null/undefined,
    // so a newer capture that explicitly recorded `connected: false` for a
    // car that really was offline at that moment is still honored as-is.
    connected: r.connected ?? true,
    usage: r.usage ?? {},
    bitmask: r.bitmask ?? r.display_map ?? {},
    positionMap: r.positionMap ?? r.position_map ?? {},
    serviceMap: r.serviceMap ?? r.service_map ?? {},
    oneHotMap: r.oneHotMap ?? {},
    // A replayed frame is history, not a live answer to a command, and legacy
    // captures predate withheld marks altogether.
    ack: NO_ACK_VERDICT,
    withheldCalls: [],
    secured: { front: [], rear: [] },
    overlaid: [],
  };
}

/**
 * Derive a UsageMap from the snapshot fields of a replay message.
 */
function deriveUsageMap(msg: SocketMessageInterface): UsageMap {
  const um: UsageMap = {};

  um[Usage.Up] = msg.direction === Usage.Up;
  um[Usage.Down] = msg.direction === Usage.Down;
  um[Usage.Moving] = msg.direction === Usage.Up || msg.direction === Usage.Down;

  um[Usage.FrontDoorOpened] = msg.doorStatus.frontOpen;
  um[Usage.FrontDoorClosed] = !msg.doorStatus.frontOpen;
  um[Usage.RearDoorOpened] = msg.doorStatus.rearOpen;
  um[Usage.RearDoorClosed] = !msg.doorStatus.rearOpen;
  um[Usage.FrontDoorStalled] = msg.doorStatus.frontStalled;
  um[Usage.RearDoorStalled] = msg.doorStatus.rearStalled;

  return um;
}

/**
 * Derive bitmask entries from the snapshot fields of a replay message.
 * In live mode, the delta pipeline populates the bitmask incrementally.
 * Replay messages lack bitmask data, so we synthesise it from direction,
 * door status, and the current msg so that the rule engine can evaluate
 * icon-definition conditions.
 */
function deriveBitmask(msg: SocketMessageInterface): Record<string, boolean> {
  const prefix = bitmaskPrefix(msg.bankId, msg.liftId);
  const bm: Record<string, boolean> = {};

  // Direction
  bm[`${prefix}Usage:Up`] = msg.direction === Usage.Up;
  bm[`${prefix}Usage:Down`] = msg.direction === Usage.Down;
  bm[`${prefix}Usage:Moving`] = msg.direction === Usage.Up || msg.direction === Usage.Down;

  // Door state
  bm[`${prefix}Usage:FrontDoorOpened`] = msg.doorStatus.frontOpen;
  bm[`${prefix}Usage:FrontDoorClosed`] = !msg.doorStatus.frontOpen;
  bm[`${prefix}Usage:RearDoorOpened`] = msg.doorStatus.rearOpen;
  bm[`${prefix}Usage:RearDoorClosed`] = !msg.doorStatus.rearOpen;
  bm[`${prefix}Usage:FrontDoorStalled`] = msg.doorStatus.frontStalled;
  bm[`${prefix}Usage:RearDoorStalled`] = msg.doorStatus.rearStalled;

  // Current msg entry (e.g. Usage:Up, Usage:FrontDoorOpened)
  const typeLabel = msgTypeLabel(msg.msg.contentType, msg.msg.messageType);
  const msgValue = resolveValue(msg.msg);
  if (typeLabel && msgValue) {
    bm[`${prefix}${typeLabel}:${msgValue}`] = msg.msg.active;
  }

  return bm;
}

/** Normalize a decoded message with safe defaults and derived bitmask. */
function normalizeMessage(record: SocketMessageInterface): SocketMessageInterface {
  const result: SocketMessageInterface = {
    bankId: record.bankId,
    liftId: record.liftId,
    serviceMode: record.serviceMode,
    direction: record.direction,
    carCalls: record.carCalls,
    hallCalls: record.hallCalls,
    position: record.position,
    faults: record.faults,
    activeFaults: record.activeFaults ?? [],
    doorStatus: {
      frontOpen: record.doorStatus?.frontOpen ?? false,
      rearOpen: record.doorStatus?.rearOpen ?? false,
      frontStalled: record.doorStatus?.frontStalled ?? false,
      rearStalled: record.doorStatus?.rearStalled ?? false,
    },
    doorZone: record.doorZone ?? null,
    levelling: record.levelling ?? false,
    escalatorControl: record.escalatorControl ?? null,
    msg: record.msg,
    connected: record.connected,
    usage: record.usage ?? {},
    bitmask: record.bitmask ?? {},
    positionMap: record.positionMap ?? {},
    serviceMap: record.serviceMap ?? {},
    oneHotMap: record.oneHotMap ?? {},
    // Preserved rather than blanked: a replayed record that carried a
    // verdict should still show it. Likewise its withheld marks; a capture made
    // before they existed carries none.
    ack: record.ack ?? NO_ACK_VERDICT,
    withheldCalls: record.withheldCalls ?? [],
    // Captures made before secured floors and overlays carry neither.
    secured: record.secured ?? { front: [], rear: [] },
    overlaid: record.overlaid ?? [],
  };

  // Derive bitmask and usage map when empty (replay mode — no delta pipeline)
  if (Object.keys(result.bitmask).length === 0) {
    result.bitmask = deriveBitmask(result);
  }
  if (Object.keys(result.usage).length === 0) {
    result.usage = deriveUsageMap(result);
  }

  return result;
}

export default function ReplayFilesPanel({ layouts }: { layouts: LayoutInterface[] }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");
  const reduxDispatch = useDispatch();
  const dispatchSocketMessage = useBankDispatch();
  const { Option } = Select;

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [replayData, setReplayData] = useState<ReplayRecord[]>([]);
  const [replayFiltered, setReplayFiltered] = useState<ReplayRecord[]>([]);
  const [jsonFileName, setJsonFileName] = useState<string>("");
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [filterType, setFilterType] = useState<"ALL" | "SCANNED" | "BANK" | "LIFT">("ALL");
  const [bankId, setBankId] = useState<string>("");
  const [liftId, setLiftId] = useState<string>("");
  const [terminalBanks, setTerminalBanks] = useState<{ id: string; name: string }[]>([]);
  const [terminalLifts, setTerminalLifts] = useState<{ id: string; name: string }[]>([]);
  const [jsonViewerType, setJsonViewerType] = useState<
    "JSON" | "PRETTY" | "BANK" | "ADAPTER" | "STATE"
  >("PRETTY");
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1.0);

  // ************************************************ */
  // REFS ******************************************* */
  const playTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekToRef = useRef<(index: number, messages: (SocketMessageInterface | null)[]) => void>(
    () => {},
  );
  // Index of the record most recently folded into BankMessagesContext, or null
  // when the accumulated fold no longer corresponds to any position in the
  // current filtered list (nothing dispatched yet, or the filter just changed).
  const lastDispatchedIndexRef = useRef<number | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const bankUuid = useMemo(() => (bankId ? stringToUuidT(bankId) : EMPTY_UUID), [bankId]);
  const liftUuid = useMemo(() => (liftId ? stringToUuidT(liftId) : EMPTY_UUID), [liftId]);
  const socketMessageBank: SocketMessageInterface =
    useLastBankMessage(bankUuid) || BLANK_SOCKET_MESSAGE;
  const socketMessageLift: SocketMessageInterface =
    useLastLiftMessage(liftUuid) || BLANK_SOCKET_MESSAGE;
  const { bank, isLoadingGetOneBank } = useGetOneBank({ id: bankId ?? "" });

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  function uploadReplayFile(onLoadCallback: (data: ReplayRecord[], fileName: string) => void) {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".json,.bin,.ln2r";
    input.onchange = (event: any) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const ext = file.name.split(".").pop()?.toLowerCase() || "";

      if (ext === "ln2r" || ext === "bin") {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const buf = e.target?.result as ArrayBuffer;
            const records = decodeReplayFile(buf);
            onLoadCallback(records, file.name);
          } catch (error: any) {
            notification.error({
              message: "Failed to decode binary replay file",
              description: error?.message || String(error),
              placement: "top",
            });
          } finally {
            input.value = "";
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text: any = e.target?.result;
            const rawMessages: any[] = JSON.parse(text);
            const base = Date.now();
            const records: ReplayRecord[] = rawMessages.map((raw, i) => ({
              timestamp: base + i * 1000,
              message: normalizeJsonRecord(raw),
            }));
            onLoadCallback(records, file.name);
          } catch (error) {
            console.error(error);
            notification.error({
              message: "Failed to parse JSON replay file",
              placement: "top",
            });
          } finally {
            input.value = "";
          }
        };
        reader.readAsText(file);
      }
    };

    input.click();
  }

  const onClickUploadReplayFile = () => {
    setIsPlaying(false);
    uploadReplayFile((data, fileName) => {
      setReplayData(data);
      setJsonFileName(fileName);
      setBankId("");
      setLiftId("");
    });
  };

  /**
   * The filtered records already normalised, so a rebuild folds ready-made
   * messages instead of re-deriving every record's bitmask and usage map.
   * Without this a single backward step would re-run `normalizeMessage` — and
   * with it `deriveBitmask`/`deriveUsageMap` — once per record in the prefix,
   * which on a long capture is a visible hitch on what is meant to be one
   * click of a Prev button.
   *
   * Entries stay index-aligned with `replayFiltered`, with `null` where a
   * record could not be normalised. That alignment is the point: the playhead
   * indexes into this array, so dropping bad records outright would silently
   * shift every position after the first malformed one.
   *
   * The per-record catch is load-bearing. `normalizeMessage` derives a bitmask
   * and a usage map, both of which walk fields an uploaded JSON file is under
   * no obligation to supply — a record with no `msg` at all throws. This runs
   * during render, where a throw takes the whole page down rather than being
   * caught by the playback handlers the way a dispatch-time throw was.
   */
  const normalizedFiltered = useMemo(
    () =>
      replayFiltered.map((record) => {
        try {
          return normalizeMessage(record.message);
        } catch (err) {
          console.error("Invalid socket message", err);
          return null;
        }
      }),
    [replayFiltered],
  );

  /**
   * Move the replay playhead to `index` and reflect it in BankMessagesContext.
   *
   * Continuing the sequence — the record immediately after the one last
   * dispatched — is a plain incremental UPDATE_MESSAGE, which is what ordinary
   * playback does frame after frame. Anything else is a discontinuity: a step
   * backwards, the wrap from the last record round to the first, or the first
   * dispatch after the filter changed. Those cannot be patched in, because the
   * context accumulates (byLift is pruned by nothing but CLEAR_MESSAGES, and
   * the four CDO maps only ever merge), so the bank roll-up would keep
   * unioning lift snapshots that the new position has not reached yet. They go
   * through REPLAY_SEEK, which refolds records 0..index from scratch.
   */
  const seekTo = useCallback(
    (index: number, messages: (SocketMessageInterface | null)[]) => {
      if (index < 0 || index >= messages.length) return;
      if (replayMoveKind(lastDispatchedIndexRef.current, index) === "step") {
        const message = messages[index];
        // An unusable record contributes nothing, so the fold stays where it
        // was — and the marker stays with it, which makes the NEXT move read
        // as a discontinuity and rebuild. Advancing the marker here would
        // claim a fold position the reducer never reached.
        if (!message) return;
        dispatchSocketMessage({ type: "UPDATE_MESSAGE", payload: message });
      } else {
        // Unusable records drop out of the payload rather than the prefix:
        // playing 0..index straight through would have skipped them too, so
        // the rebuilt fold still matches what playback would have produced.
        dispatchSocketMessage({
          type: "REPLAY_SEEK",
          payload: messages.slice(0, index + 1).filter((m): m is SocketMessageInterface => !!m),
        });
      }
      lastDispatchedIndexRef.current = index;
    },
    [dispatchSocketMessage],
  );
  seekToRef.current = seekTo;

  const prevMessage = () => {
    setIsPlaying(false);
    if (replayFiltered.length === 0) return;
    try {
      const prevIndex = (currentIndex - 1 + replayFiltered.length) % replayFiltered.length;
      seekTo(prevIndex, normalizedFiltered);
      setCurrentIndex(prevIndex);
    } catch (err) {
      console.error("Invalid socket message", err);
    }
  };

  const nextMessage = () => {
    setIsPlaying(false);
    if (replayFiltered.length === 0) return;
    try {
      const nextIndex = (currentIndex + 1) % replayFiltered.length;
      seekTo(nextIndex, normalizedFiltered);
      setCurrentIndex(nextIndex);
    } catch (err) {
      console.error("Invalid socket message", err);
    }
  };

  const onClickPlayPause = () => {
    if (isPlaying) {
      setIsPlaying(false);
    } else if (replayFiltered.length > 0) {
      // Dispatch the current frame immediately on play
      seekTo(currentIndex, normalizedFiltered);
      setIsPlaying(true);
    }
  };

  const launchNotification = () => {
    notification.success({
      message: "Text copied to clipboard",
      placement: "top",
      className: `monitoring__notification__${theme}`,
    });
  };

  const onChangeRadio = (e: any) => {
    setJsonViewerType(e.target.value);
  };

  /** Get the current message for display in viewers. */
  const currentMessage = (): SocketMessageInterface | null => {
    if (replayFiltered.length === 0) return null;
    return replayFiltered[currentIndex]?.message ?? null;
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    const bankSet = new Map<string, string>();
    const liftSet = new Map<string, string>();
    replayData.forEach((r) => {
      const bid = uuidToString(r.message.bankId);
      const lid = uuidToString(r.message.liftId);
      if (bid && !bankSet.has(bid)) {
        const layout = layouts.find((l) => l.idBank === bid);
        bankSet.set(bid, layout?.name ?? bid);
      }
      if (lid && !liftSet.has(lid)) {
        const layout = layouts.find((l) => l.lifts.some((lf) => String(lf.id) === lid));
        const lift = layout?.lifts.find((lf) => String(lf.id) === lid);
        const label = lift ? `${layout?.name} - ${lift.name}` : lid;
        liftSet.set(lid, label);
      }
    });
    const banks = Array.from(bankSet, ([id, name]) => ({ id, name }));
    setTerminalBanks(banks);
    setTerminalLifts(Array.from(liftSet, ([id, name]) => ({ id, name })));

    // Auto-select filter based on scanned content
    const scannedIds = banks.map((b) => b.id);
    reduxDispatch(updateReplayScannedBankIds(scannedIds));
    if (replayData.length > 0) {
      if (banks.length === 1) {
        setFilterType("BANK");
        setBankId(banks[0].id);
        setLiftId("");
        reduxDispatch(updateReplayFilterBankId(banks[0].id));
      } else {
        setFilterType("SCANNED");
        setBankId("");
        setLiftId("");
        reduxDispatch(updateReplayFilterBankId(""));
      }
    }
  }, [replayData, layouts, reduxDispatch]);

  useEffect(() => {
    if (filterType === "ALL" || filterType === "SCANNED") {
      setReplayFiltered(replayData);
    } else if (filterType === "BANK" && bankId !== "") {
      setReplayFiltered(replayData.filter((r) => uuidToString(r.message.bankId) === bankId));
    } else if (filterType === "LIFT" && liftId !== "") {
      setReplayFiltered(replayData.filter((r) => uuidToString(r.message.liftId) === liftId));
    } else {
      setReplayFiltered([]);
    }
    setCurrentIndex(0);
    setIsPlaying(false);
    // The accumulated fold describes the PREVIOUS filtered list, so no index
    // in the new one continues it. Forget the marker and the next dispatch
    // rebuilds instead of patching a fold that includes records the new
    // filter excludes.
    lastDispatchedIndexRef.current = null;
  }, [filterType, bankId, liftId, replayData]);

  // Playback effect: schedules the next frame based on timestamp deltas.
  // Uses seekToRef to avoid re-triggering when seekTo changes identity
  // (which would cancel the pending timeout).
  useEffect(() => {
    if (!isPlaying || replayFiltered.length === 0) return undefined;

    if (currentIndex + 1 >= replayFiltered.length) {
      setIsPlaying(false);
      return undefined;
    }

    const currentTs = replayFiltered[currentIndex].timestamp;
    const nextTs = replayFiltered[currentIndex + 1].timestamp;
    const speed = playbackSpeed > 0 ? playbackSpeed : 1;
    const delay = Math.max(0, (nextTs - currentTs) / speed);

    const timeout = setTimeout(() => {
      const nextIdx = currentIndex + 1;
      seekToRef.current(nextIdx, normalizedFiltered);
      setCurrentIndex(nextIdx);
    }, delay);

    playTimeoutRef.current = timeout;
    return () => clearTimeout(timeout);
  }, [isPlaying, currentIndex, replayFiltered, normalizedFiltered, playbackSpeed]);

  // ************************************************ */
  // COMPONENT ************************************** */
  const msg = currentMessage();

  const getPlayButtonClass = () => {
    if (replayFiltered.length === 0) return "";
    return isPlaying ? "buttonStyle__4" : "buttonStyle__17";
  };

  return (
    <div className="generalStyles__flex">
      <div
        className={`monitoring__card__${theme}`}
        style={{ margin: "10px", padding: "10px", width: "450px" }}
      >
        <div>
          <div className="generalStyles__flex">
            <div style={{ width: "250px", color: "#2968c0", fontWeight: "bolder" }}>
              {t("general.fileName")}:
            </div>
            <div className="generalStyles__width100">
              <Tag color="blue" className="generalStyles__width100" style={{ textAlign: "center" }}>
                {jsonFileName || "--"}
              </Tag>
            </div>
          </div>
          <div className="generalStyles__flex mt-2">
            <div style={{ width: "250px", color: "#2968c0", fontWeight: "bolder" }}>
              {t("general.totalRecords")}:
            </div>
            <div className="generalStyles__width100">
              <Tag
                color="purple"
                className="generalStyles__width100"
                style={{ textAlign: "center" }}
              >
                {replayData.length}
              </Tag>
            </div>
          </div>
          <div className="generalStyles__flex mt-2">
            <div style={{ width: "250px", color: "#2968c0", fontWeight: "bolder" }}>
              {t("general.filteredRecords")}:
            </div>
            <div className="generalStyles__width100">
              <Tag
                color="orange"
                className="generalStyles__width100"
                style={{ textAlign: "center" }}
              >
                {replayFiltered.length}
              </Tag>
            </div>
          </div>
          <div className="generalStyles__flex mt-2">
            <div style={{ width: "250px", color: "#2968c0", fontWeight: "bolder" }}>Record:</div>
            <div className="generalStyles__width100">
              <Tag
                color="green"
                className="generalStyles__width100"
                style={{ textAlign: "center" }}
              >
                {replayFiltered.length > 0 ? currentIndex + 1 : 0}
              </Tag>
            </div>
          </div>
          {replayFiltered.length > 0 && (
            <div className="generalStyles__flex mt-2">
              <div
                style={{
                  width: "250px",
                  color: "#2968c0",
                  fontWeight: "bolder",
                }}
              >
                Status:
              </div>
              <div className="generalStyles__width100">
                <Tag
                  color={isPlaying ? "green" : "volcano"}
                  className="generalStyles__width100"
                  style={{ textAlign: "center", fontWeight: "bold" }}
                >
                  {isPlaying ? "PLAYING" : "PAUSED"}
                </Tag>
              </div>
            </div>
          )}
        </div>

        <div className="mt-2">
          <Button
            className="buttonStyle__28 generalStyles__width100"
            onClick={onClickUploadReplayFile}
            icon={<FontAwesomeIcon icon={faUpload} className="generalStyles__mrFix" />}
            size="small"
          >
            {t("general.uploadReplayFile")}
          </Button>
        </div>
        <div className="mt-2">
          <Select
            className="generalStyles__width100 generalStyles__textCenter "
            showSearch
            optionFilterProp="children"
            allowClear={false}
            value={filterType}
            placeholder={t("general.noLiftSelected")}
            size="small"
            disabled={replayData.length === 0}
            onChange={(e: "ALL" | "SCANNED" | "BANK" | "LIFT") => {
              setFilterType(e);
              setBankId("");
              setLiftId("");
              setCurrentIndex(0);
              reduxDispatch(updateReplayFilterBankId(""));
            }}
          >
            {["SCANNED", "ALL", "BANK", "LIFT"].map((item: string) => (
              <Option key={item} value={item}>
                {item}
              </Option>
            ))}
          </Select>
        </div>
        <div className="mt-2">
          {filterType === "BANK" && (
            <Select
              className="generalStyles__width100 generalStyles__textCenter "
              showSearch
              optionFilterProp="children"
              allowClear={false}
              value={bankId}
              size="small"
              placeholder={t("general.noLiftSelected")}
              disabled={replayData.length === 0}
              onChange={(e: string) => {
                setBankId(e);
                setLiftId("");
                reduxDispatch(updateReplayFilterBankId(e));
              }}
            >
              {terminalBanks.map((item: { id: string; name: string }) => (
                <Option key={formatUuidT(item.id)} value={formatUuidT(item.id)}>
                  {item.name}
                </Option>
              ))}
            </Select>
          )}
        </div>
        <div className="mt-2">
          {filterType === "LIFT" && (
            <Select
              className="generalStyles__width100 generalStyles__textCenter "
              showSearch
              optionFilterProp="children"
              allowClear={false}
              value={liftId}
              size="small"
              placeholder={t("general.noLiftSelected")}
              disabled={replayData.length === 0}
              onChange={(e: string) => {
                setLiftId(e);
                setBankId("");
                const parentLayout = layouts.find((l) => l.lifts.some((lf) => String(lf.id) === e));
                reduxDispatch(updateReplayFilterBankId(parentLayout?.idBank ?? ""));
              }}
            >
              {terminalLifts.map((item: { id: string; name: string }) => (
                <Option key={formatUuidT(item.id)} value={formatUuidT(item.id)}>
                  {item.name}
                </Option>
              ))}
            </Select>
          )}
        </div>
        {/* Playback speed */}
        <div className="mt-2 generalStyles__flex" style={{ alignItems: "center" }}>
          <div
            style={{
              color: "#2968c0",
              fontWeight: "bolder",
              marginRight: "8px",
              whiteSpace: "nowrap",
            }}
          >
            Speed:
          </div>
          <InputNumber
            min={0.1}
            max={100}
            step={0.5}
            value={playbackSpeed}
            onChange={(val) => setPlaybackSpeed(val ?? 1.0)}
            size="small"
            style={{ width: "100%" }}
          />
        </div>
        {/* Playback controls */}
        <div className="mt-2 generalStyles__flex">
          <div className="generalStyles__width100" style={{ marginRight: "5px" }}>
            <Button
              disabled={replayFiltered.length === 0}
              className={`${
                replayFiltered.length === 0 || currentIndex === 0 ? "" : "buttonStyle__28"
              } generalStyles__width100`}
              onClick={prevMessage}
              size="small"
            >
              <FontAwesomeIcon icon={faBackward} className="generalStyles__mrFix" />
              <span>Prev</span>
            </Button>
          </div>
          <div
            className="generalStyles__width100"
            style={{ marginRight: "5px", marginLeft: "5px" }}
          >
            <Button
              disabled={replayFiltered.length === 0}
              className={`${getPlayButtonClass()} generalStyles__width100`}
              onClick={onClickPlayPause}
              size="small"
            >
              <FontAwesomeIcon
                icon={isPlaying ? faPause : faPlay}
                className="generalStyles__mrFix"
              />
              <span>{isPlaying ? "Pause" : "Play"}</span>
            </Button>
          </div>
          <div className="generalStyles__width100" style={{ marginLeft: "5px" }}>
            <Button
              disabled={replayFiltered.length === 0}
              className={`${
                replayFiltered.length === 0 || currentIndex + 1 === replayFiltered.length
                  ? ""
                  : "buttonStyle__28"
              } generalStyles__width100`}
              onClick={nextMessage}
              size="small"
            >
              <FontAwesomeIcon icon={faForward} className="generalStyles__mrFix" />
              <span>Next</span>
            </Button>
          </div>
        </div>
      </div>
      <div
        className={`monitoring__card__${theme}`}
        style={{ margin: "10px", padding: "10px", overflowX: "auto" }}
      >
        <div>
          <Radio.Group onChange={onChangeRadio} value={jsonViewerType}>
            <Radio value="ADAPTER">ADAPTER</Radio>
            <Radio value="BANK">BANK</Radio>
            <Radio value="JSON">JSON</Radio>
            <Radio value="PRETTY">PRETTY</Radio>
            <Radio value="STATE">STATE</Radio>
          </Radio.Group>
        </div>
        {jsonViewerType === "ADAPTER" && (
          <div className="mt-3">
            <IconDefinitionRules
              adapter={bank?.adapter ?? BLANK_ADAPTER}
              isLoading={isLoadingGetOneBank}
            />
          </div>
        )}
        {jsonViewerType === "BANK" && (
          <div className="mt-3">
            <BankDetailsCard bank={bank || BLANK_BANK} isLoadingGetOneBank={isLoadingGetOneBank} />
          </div>
        )}
        {jsonViewerType === "JSON" && (
          <div className="mt-3">
            {filterType === "BANK" && (
              <CopyToClipboard
                text={JSON.stringify(socketMessageBank, bigIntReplacer)}
                onCopy={launchNotification}
              >
                <Tooltip placement="top" title={t("general.copyToClipboard")}>
                  <span style={{ cursor: "pointer" }}>
                    {JSON.stringify(socketMessageBank, bigIntReplacer)}
                  </span>
                </Tooltip>
              </CopyToClipboard>
            )}
            {filterType === "LIFT" && (
              <CopyToClipboard
                text={JSON.stringify(socketMessageLift, bigIntReplacer)}
                onCopy={launchNotification}
              >
                <Tooltip placement="top" title={t("general.copyToClipboard")}>
                  <span style={{ cursor: "pointer" }}>
                    {JSON.stringify(socketMessageLift, bigIntReplacer)}
                  </span>
                </Tooltip>
              </CopyToClipboard>
            )}
            {(filterType === "ALL" || filterType === "SCANNED") && msg && (
              <CopyToClipboard
                text={JSON.stringify(msg, bigIntReplacer)}
                onCopy={launchNotification}
              >
                <Tooltip placement="top" title={t("general.copyToClipboard")}>
                  <span style={{ cursor: "pointer" }}>{JSON.stringify(msg, bigIntReplacer)}</span>
                </Tooltip>
              </CopyToClipboard>
            )}
          </div>
        )}
        {jsonViewerType === "PRETTY" && (
          <div className="mt-3">
            {filterType === "BANK" && (
              <ReplayFilesPrettyJson
                liftType={bank.type ?? LiftType.ELEVATOR}
                socketMessage={socketMessageBank}
              />
            )}
            {filterType === "LIFT" && (
              <ReplayFilesPrettyJson
                socketMessage={socketMessageLift}
                liftType={bank.type ?? LiftType.ELEVATOR}
              />
            )}
            {(filterType === "ALL" || filterType === "SCANNED") && msg && (
              <ReplayFilesPrettyJson
                socketMessage={msg}
                liftType={bank.type ?? LiftType.ELEVATOR}
              />
            )}
          </div>
        )}
        {jsonViewerType === "STATE" && (
          <div className="mt-3">
            {filterType === "BANK" && (
              <ControlPanelStateView bankId={bankId} bankLifts={bank?.lifts || []} />
            )}
            {filterType === "LIFT" && <ControlPanelStateView liftId={liftId} />}
            {(filterType === "ALL" || filterType === "SCANNED") && msg && (
              <ControlPanelStateView bankId={uuidToString(msg.bankId)} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
